import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Panel } from "@/components/admin/ui";
import { BillingDetail } from "@/components/billing/BillingDetail";
import { usePolling } from "@/hooks/usePolling";
import { LastUpdated } from "@/components/common/LastUpdated";

export default function OrgBilling() {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback((silent) => {
    axios.get(`${API}/org/billing`, { withCredentials: true })
      .then(({ data }) => { setData(data); setLastUpdated(Date.now()); })
      .catch((err) => { if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail)); });
  }, [API]);

  useEffect(() => { load(); }, [load]);

  usePolling(() => load(true), 10000);

  const viewQuote = () => {
    const token = data?.quote?.access_token;
    if (token) window.open(`/quote/${token}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="org-billing-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white">Nova Plan & Billing</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Your organization's billing relationship with Nova Opsis.</p>
        </div>
        <LastUpdated timestamp={lastUpdated} testId="org-billing-last-updated" />
      </div>

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-billing-error">{error}</p>}

      {!data ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="org-billing-loading">
          {error ? "" : "Loading billing information…"}
        </div>
      ) : (
        <Panel testId="org-billing-panel">
          <BillingDetail
            orgName={data.organization.name}
            billing={data.billing}
            quote={data.quote}
            solutionItems={data.organization.solution_items || []}
            onViewQuote={viewQuote}
            testId="org-billing-detail"
          />
        </Panel>
      )}
    </div>
  );
}
